import type { ComponentChildren, CSSProperties } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'

import { ChevronIcon } from '#src/assets/ui/icons/ChevronIcon.tsx'
import { captionStyle, uiTokens } from '#src/assets/ui/shared/styles.ts'

export function ExpandableSection({
  icon,
  title,
  children,
}: {
  icon: ComponentChildren
  title: string
  children: ComponentChildren
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isHeaderHovered, setIsHeaderHovered] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (containerRef.current) {
      updateExpandedContentPadding(containerRef.current)
    }
  }, [isExpanded])

  const toggleExpanded = (): void => {
    setIsExpanded(prev => !prev)
  }

  const handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    toggleExpanded()
  }

  const buttonStyle: CSSProperties = {
    ...headerStyle,
    ...(isHeaderHovered ? { background: uiTokens.colors.gray100 } : {}),
  }

  return (
    <div ref={containerRef} style={expandableStyle}>
      <button
        type="button"
        style={buttonStyle}
        aria-expanded={isExpanded}
        onClick={event => {
          event.preventDefault()
          event.stopPropagation()
          toggleExpanded()
        }}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setIsHeaderHovered(true)}
        onMouseLeave={() => setIsHeaderHovered(false)}
      >
        <div style={iconStyle}>{icon}</div>
        <div style={titleStyle}>{title}</div>
        <div
          style={{
            ...chevronStyle,
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          <ChevronIcon />
        </div>
      </button>
      <div
        data-lcc-expandable-content
        style={contentStyle(isExpanded)}
        aria-hidden={!isExpanded}
      >
        <div data-lcc-expandable-text style={textStyle}>
          {children}
        </div>
      </div>
    </div>
  )
}

function updateExpandedContentPadding(container: HTMLElement) {
  const expandableGroup = container.parentElement
  if (!expandableGroup) {
    return
  }

  const expandedContents = Array.from(
    expandableGroup.querySelectorAll<HTMLElement>(
      '[data-lcc-expandable-content][aria-hidden="false"]',
    ),
  )
  const padding = expandedContents.length > 1 ? '1.5rem 1.5rem 4rem' : '1.5rem'

  expandedContents.forEach(expandedContent => {
    const text = expandedContent.querySelector<HTMLElement>(
      '[data-lcc-expandable-text]',
    )
    if (text) {
      text.style.padding = padding
    }
  })
}

const expandableStyle: CSSProperties = {
  border: `1px solid ${uiTokens.colors.gray200}`,
  borderRadius: uiTokens.radius.sm,
  boxSizing: 'border-box',
  maxWidth: '100%',
  minHeight: '0',
  minWidth: '0',
  overflow: 'hidden',
  width: '100%',
}

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '0.875rem 1rem',
  cursor: 'pointer',
  background: uiTokens.colors.white,
  transition: 'background-color 0.15s ease',
  gap: '0.75rem',
  boxSizing: 'border-box',
  minWidth: '0',
  width: '100%',
  border: 'none',
  textAlign: 'left',
}

const iconStyle: CSSProperties = {
  display: 'flex',
  color: uiTokens.colors.gray500,
  flexShrink: '0',
}

const titleStyle: CSSProperties = {
  fontSize: '0.9em',
  fontWeight: '500',
  color: uiTokens.colors.gray900,
  flex: '1',
  minWidth: '0',
  overflowWrap: 'anywhere',
}

const chevronStyle: CSSProperties = {
  display: 'flex',
  color: uiTokens.colors.gray400,
  transition: 'transform 0.25s ease',
  flexShrink: '0',
}

const contentStyle = (isExpanded: boolean): CSSProperties => ({
  maxHeight: isExpanded ? '150px' : '0',
  maxWidth: '100%',
  minHeight: '0',
  minWidth: '0',
  overflowX: isExpanded ? 'auto' : 'hidden',
  overflowY: isExpanded ? 'auto' : 'hidden',
  overscrollBehavior: 'contain',
  transition: isExpanded
    ? 'max-height 0.3s ease'
    : 'max-height 0.3s ease, overflow-y 0s 0.3s',
  borderTop: isExpanded
    ? `1px solid ${uiTokens.colors.gray200}`
    : `0px solid ${uiTokens.colors.gray200}`,
  background: uiTokens.colors.gray50,
  width: '100%',
  display: isExpanded ? 'block' : 'none',
})

const textStyle: CSSProperties = {
  ...captionStyle,
  boxSizing: 'border-box',
  padding: '1.5rem',
  maxWidth: '100%',
  minWidth: '0',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  width: '100%',
}
